/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  createAsyncThunk,
  createDraftSafeSelector,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';
import apiClient from '../../../services/api-client';
import { RootState } from '../../../store/rootReducer';
import _ from 'lodash';
import {
  AccountListApiResponse,
  AuthState,
  DigitalFarmerAccountDictionary,
  AuthAccount,
  DigitalFarmerAccount,
  AtomicTokenApiResponse,
  RelationshipOwner,
  AccountApiResponse,
  UserDetailsApiResponse,
} from './types';
import { storageDb } from '../../../services/localforage';
import { AccountInfo } from '@azure/msal-browser';
import storageSession from 'redux-persist/lib/storage/session';
import { ERequestStatus } from '../../../util/types';
import { isPlatformMobile } from '../../../util/platform';

export const getAccounts = createAsyncThunk<AccountListApiResponse, void>(
  'auth/list',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const timeout = state.remoteConfig.authorisationTimeouts.getAccounts;
    const request = await apiClient.get<AccountListApiResponse, AxiosRequestConfig>(
      '/v2/accounts',
      {
        timeout,
      },
    );
    return request.data;
  },
);

export const getAccountDetails = createAsyncThunk<AccountApiResponse | undefined, void>(
  'auth/details',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const selectedFarmAccountNumber = selectCurrentAccountNumber(state) ?? '';
    if (selectedFarmAccountNumber) {
      const timeout = state.remoteConfig.authorisationTimeouts.getAccountDetails;
      const request = await apiClient.get<AccountApiResponse, AxiosRequestConfig>(
        `/v1/accounts/${selectedFarmAccountNumber}/details`,
        {
          timeout,
        },
      );
      return request.data;
    }
  },
);

export const getUserDetails = createAsyncThunk<UserDetailsApiResponse, void>(
  'auth/user-details',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const timeout = state.remoteConfig.authorisationTimeouts.getUserDetails;
    const request = await apiClient.get<UserDetailsApiResponse, AxiosRequestConfig>(
      '/v1/user-details',
      {
        timeout,
      },
    );
    return request.data;
  },
);

export const apiGetAtomicToken = async (timeout: number) => {
  const request = await apiClient.post<AtomicTokenApiResponse, AxiosRequestConfig>(
    '/atomic/token',
    undefined,
    {
      timeout,
    },
  );
  return request.data;
};

export const authPersistConfig = {
  key: 'auth',
  // serialize: false,
  storage: isPlatformMobile() ? storageDb : storageSession,
};

export const initialState: AuthState = {
  accounts: {},
  listStatus: '',
  detailsStatus: '',
  userDetailsStatus: '',
  token: null,
  isCEUser: false,
  atomicJWTToken: null,
  selectedAccountId: null,
  userDetails: null,
};

const AuthSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  extraReducers(builder) {
    builder.addCase(getAccounts.pending, (state) => {
      state.listStatus = ERequestStatus.PENDING;
    });

    builder.addCase(getAccounts.rejected, (state) => {
      state.listStatus = ERequestStatus.REJECTED;
      state.accounts[state.selectedAccountId as string].digitalFarmer = undefined;
    });
    builder.addCase(getAccounts.fulfilled, (state, action) => {
      let _default = '';
      let hasAtleastOneTFOAccount = false;

      let _proxyBlend: DigitalFarmerAccountDictionary = {};

      action.payload.accounts.forEach((acc) => {
        if (acc.isDefaultAccountForUser) {
          _default = acc.accountNumber;
        }

        if (acc.hasTfoAccess) {
          hasAtleastOneTFOAccount = true;
        }

        _proxyBlend = {
          ..._proxyBlend,
          [acc.accountNumber]: {
            ...acc,
          },
        };
      });

      if (_default === '' && action.payload.accounts.length > 0) {
        _default = action.payload.accounts[0].accountNumber;
      }

      const currentSelected =
        state.accounts[state.selectedAccountId as string]?.digitalFarmer?.selectedAccountNumber ??
        null;

      const localData = state.accounts[state.selectedAccountId as string]?.digitalFarmer?.accounts;

      if (localData) {
        const keys = Object.keys(localData);
        keys.forEach((key) => {
          if (_proxyBlend[key] && localData[key].lastViewedAt) {
            _proxyBlend[key].lastViewedAt = new Date(localData[key].lastViewedAt);
          }
        });
      }

      const previousSelectedAccount =
        state.accounts[state.selectedAccountId as string].digitalFarmer?.selectedAccount;

      delete state.accounts[state.selectedAccountId as string].digitalFarmer;

      if (currentSelected) {
        if (_proxyBlend[currentSelected]) {
          _default = currentSelected;
        }
      }

      const { digitalFarmer }: Pick<AuthAccount, 'digitalFarmer'> = {
        digitalFarmer: {
          accounts: _proxyBlend,
          selectedAccountNumber: _default,
          // copy over the previous selected account information so that we have it till the api returns the updated version
          selectedAccount: previousSelectedAccount,
          isTfo: hasAtleastOneTFOAccount,
        },
      };

      state.accounts[state.selectedAccountId as string].digitalFarmer = digitalFarmer;
      state.listStatus = ERequestStatus.FULFILLED;
    });
    builder.addCase(getAccountDetails.pending, (state) => {
      state.detailsStatus = ERequestStatus.PENDING;
    });

    builder.addCase(getAccountDetails.rejected, (state) => {
      state.detailsStatus = ERequestStatus.REJECTED;
      state.accounts[state.selectedAccountId as string].digitalFarmer!.selectedAccount = undefined;
    });
    builder.addCase(getAccountDetails.fulfilled, (state, action) => {
      if (state.accounts[state.selectedAccountId as string].digitalFarmer) {
        state.accounts[state.selectedAccountId as string].digitalFarmer!.selectedAccount =
          action.payload?.account;
      }
      state.detailsStatus = ERequestStatus.FULFILLED;
    });
    builder.addCase(getUserDetails.pending, (state) => {
      state.userDetailsStatus = ERequestStatus.PENDING;
    });
    builder.addCase(getUserDetails.rejected, (state) => {
      state.userDetailsStatus = ERequestStatus.REJECTED;
      state.userDetails = null;
    });
    builder.addCase(getUserDetails.fulfilled, (state, action) => {
      state.userDetails = action.payload;
      state.userDetailsStatus = ERequestStatus.FULFILLED;
    });
  },
  reducers: {
    setAtomicToken: (state, action: PayloadAction<string>) => {
      state.atomicJWTToken = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setSelectedAuthAccount: (state, action: PayloadAction<string>) => {
      state.selectedAccountId = action.payload;
    },
    setSelectedFarmerAccount: (state, action: PayloadAction<string>) => {
      if (
        state.selectedAccountId &&
        state.accounts[state.selectedAccountId].digitalFarmer &&
        state.accounts[state.selectedAccountId].digitalFarmer?.accounts[action.payload]
      ) {
        state.accounts[state.selectedAccountId].digitalFarmer!.selectedAccountNumber =
          action.payload;
        state.accounts[state.selectedAccountId].digitalFarmer!.accounts[
          action.payload
        ].lastViewedAt = new Date();
      }
    },
    setAuthAccount: (state, action: PayloadAction<AccountInfo>) => {
      state.accounts = _.merge(state.accounts, {
        [action.payload.homeAccountId]: {
          ...action.payload,
        },
      });
      state.isCEUser = action.payload.tenantId === process.env.REACT_APP_AZURE_CE_B2C_TENANT_ID;

      AuthSlice.caseReducers.setSelectedAuthAccount(
        state,
        AuthSlice.actions.setSelectedAuthAccount(action.payload.homeAccountId),
      );
    },
    addAuthAccount: (state, action: PayloadAction<AuthAccount>) => {
      state.accounts = _.merge(state.accounts, {
        [action.payload.homeAccountId]: {
          ...action.payload,
        },
      });
    },
    addAuthAccounts: (state, action: PayloadAction<AuthAccount[]>) => {
      let accounts = {};
      action.payload.forEach((acc) => {
        accounts = {
          ...accounts,
          [acc.homeAccountId]: {
            ...acc,
          },
        };
      });
      const __ = _.merge(accounts, state.accounts);
      state.accounts = __;
    },
  },
});

const selectSelf = (state: RootState) => state.auth;

export const selectCurrentAccountClaims = (state: RootState) => {
  return state.auth.accounts[state.auth.selectedAccountId as string]?.idTokenClaims;
};

export const selectIsCEUser = createSelector(selectSelf, (state) => state.isCEUser);

export const selectEmailForPrefill = createSelector(selectSelf, (state) => {
  const digifarmer = state.accounts[state.selectedAccountId as string].digitalFarmer;
  if (!digifarmer) {
    const claims = state.accounts[state.selectedAccountId as string].idTokenClaims;
    return claims?.emails ?? claims?.username;
  }
  if (digifarmer.accounts[digifarmer.selectedAccountNumber].hasTfoAccess) {
    return digifarmer.selectedAccount?.contact?.email;
  } else {
    const claims = state.accounts[state.selectedAccountId as string].idTokenClaims;
    return claims?.emails ?? claims?.username;
  }
});

export const selectLoginEmail = createSelector(selectSelf, (state): string | null => {
  const claims = state.accounts[state.selectedAccountId as string].idTokenClaims;
  return claims?.emails ?? claims?.username ?? null;
});

export const selectNameForPrefill = createSelector(selectSelf, (state) => {
  const digitFarmer = state.accounts[state.selectedAccountId as string].digitalFarmer;
  if (!digitFarmer) {
    const claims = state.accounts[state.selectedAccountId as string].idTokenClaims;
    return `${claims?.given_name ?? ''} ${claims?.family_name ?? ''}`;
  }
  if (digitFarmer.selectedAccount?.hasTfoAccess) {
    return digitFarmer.selectedAccount?.contact?.name;
  } else {
    const claims = state.accounts[state.selectedAccountId as string].idTokenClaims;
    return `${claims?.given_name ?? ''} ${claims?.family_name ?? ''}`;
  }
});

export const selectPhoneForPrefill = createSelector(selectSelf, (state) => {
  const selectedFarmAccount =
    state.accounts[state.selectedAccountId as string].digitalFarmer?.selectedAccount;
  if (selectedFarmAccount?.hasTfoAccess) {
    if (selectedFarmAccount.contact?.phone) {
      return selectedFarmAccount.contact.phone;
    }
    return selectedFarmAccount?.primaryPhoneNumber?.phoneNumber ?? null;
  } else {
    return null;
  }
});

export const selectName = createSelector(selectCurrentAccountClaims, (state) => {
  return `${state?.given_name ?? ''} ${state?.family_name ?? ''}`;
});

export const selectEmail = createSelector(
  selectCurrentAccountClaims,
  (state) => `${state.emails ?? state.username}`,
);

export const selectCurrentAccountId = createDraftSafeSelector(
  selectSelf,
  (state) => state.selectedAccountId,
);
export const selectToken = createSelector(selectSelf, (state) => state.token);
export const selectAtomicToken = createSelector(selectSelf, (state) => state.atomicJWTToken);

export const { addAuthAccount, addAuthAccounts, setAuthAccount, setSelectedAuthAccount } =
  AuthSlice.actions;

export const selectAllFarmerAccounts = createSelector(
  selectSelf,
  (state) => state.accounts[state.selectedAccountId as string]?.digitalFarmer,
);

export const selectCurrentAccountNumber = createDraftSafeSelector(selectSelf, (state) => {
  const acc = state.accounts[state.selectedAccountId as string]?.digitalFarmer;
  if (acc) {
    return acc.selectedAccountNumber;
  }
  return undefined;
});

export const selectCurrentFarmerAccount = createDraftSafeSelector(selectSelf, (state) => {
  const digitalFarmer = state.accounts[state.selectedAccountId as string]?.digitalFarmer;
  if (digitalFarmer && state.detailsStatus === ERequestStatus.FULFILLED) {
    if (digitalFarmer?.selectedAccountNumber === digitalFarmer?.selectedAccount?.accountNumber) {
      return digitalFarmer?.selectedAccount;
    }
  }
  return undefined;
});

export const selectUserContactDetails = createDraftSafeSelector(selectSelf, (state) => {
  if (state.userDetailsStatus === ERequestStatus.FULFILLED) {
    return state.userDetails;
  }
});

export const selectIsLoadingAuth = createSelector(selectSelf, (state) => {
  return [state.listStatus, state.detailsStatus, state.userDetailsStatus].some(
    (status) => status === ERequestStatus.PENDING,
  );
});

export const selectIsThisTfo = createSelector(selectCurrentFarmerAccount, (account) => {
  return account?.hasTfoAccess;
});

export const getProperty = (state: RootState) => {
  if (state.auth.selectedAccountId) {
    const accountId = state.auth.selectedAccountId;
    const propertyId = state.auth.accounts[accountId].digitalFarmer?.selectedAccountNumber;
    return propertyId;
  }
};

export const selectContactInfo = createDraftSafeSelector(
  selectCurrentFarmerAccount,
  (state): RelationshipOwner => {
    let name: string | undefined = undefined;

    if (state?.relationshipOwner?.email) {
      if (state.relationshipOwner?.fullName) {
        name = `${state.relationshipOwner?.fullName}`.trim();
      }

      return {
        fullName: name,
        email: state.relationshipOwner?.email,
        phone: undefined,
      };
    }

    return {
      email: 'ask@farmlands.co.nz',
      phone: '0800 200 600',
    };
  },
);

export const isMultiAccount = createSelector(selectCurrentFarmerAccount, (farmerAccounts) => {
  if (farmerAccounts) return Object.keys(farmerAccounts).length > 1;

  return false;
});

export const selectCanUseQuickOrder = createDraftSafeSelector(
  selectCurrentFarmerAccount,
  (farmerAccounts) => {
    return farmerAccounts?.hasCheckoutAccess;
  },
);

export const selectHomeBranch = createDraftSafeSelector(
  selectCurrentFarmerAccount,
  (state: DigitalFarmerAccount | undefined): string | undefined => {
    return state?.homeBranchId;
  },
);

export default AuthSlice;
